import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Lock, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  useSandboxSession,
  markSandboxLeadCaptured,
} from '../hooks/useSandboxSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+\-()\s]{6,20}$/;

/**
 * DemoLeadCaptureModal — interrupt-and-capture modal shown to sandbox
 * users the first time they trigger a high-intent action (Download PDF,
 * Print, Send). After successful capture the requested action is
 * resumed automatically.
 *
 * Controlled via `open` + `onOpenChange`. The `onSubmitted` callback
 * fires after a successful POST so the parent can resume the deferred
 * action without re-prompting.
 */
export function DemoLeadCaptureModal({
  open,
  onOpenChange,
  onSubmitted,
  reason = 'unlock document exporting',
  accentColor = '#f59e0b',
}) {
  const { isSandbox, tier, organizationId } = useSandboxSession();
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim() || !EMAIL_RE.test(form.email.trim())) e.email = 'Enter a valid email';
    if (!form.phone.trim() || !PHONE_RE.test(form.phone.trim())) e.phone = 'Enter a valid phone';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/demo-leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        selected_tier: tier || null,
        associated_temp_org: organizationId || null,
      });
      markSandboxLeadCaptured();
      toast.success('Saved! Your work is yours to keep.');
      onSubmitted?.();
      onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail || 'Could not save details — please retry';
      toast.error(typeof d === 'string' ? d : 'Could not save details');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="demo-lead-modal-overlay"
      className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm"
      onClick={() => !busy && onOpenChange(false)}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="demo-lead-modal"
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        <div
          className="px-6 py-5 text-white relative"
          style={{ background: `linear-gradient(135deg, #0f172a 0%, ${accentColor} 180%)` }}
        >
          <button
            type="button"
            onClick={() => !busy && onOpenChange(false)}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
            data-testid="demo-lead-modal-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: `${accentColor}33`, border: `1px solid ${accentColor}88` }}
            >
              <Lock className="w-5 h-5" style={{ color: accentColor }} />
            </div>
            <div>
              <h2 className="text-lg font-bold">Unlock this action</h2>
              <p className="text-xs text-white/80 mt-0.5">
                You're trying to {reason} from a demo sandbox.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3 leading-relaxed">
            Enter your details to instantly unlock document exporting and save your testing profile.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="dl-name" className="text-slate-700 text-sm">Full Name</Label>
            <Input
              id="dl-name"
              data-testid="demo-lead-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
              className="h-11"
            />
            {errors.name && <p className="text-xs text-red-600">{errors.name}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dl-email" className="text-slate-700 text-sm">Email Address</Label>
            <Input
              id="dl-email"
              data-testid="demo-lead-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="h-11"
            />
            {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dl-phone" className="text-slate-700 text-sm">Phone Number</Label>
            <Input
              id="dl-phone"
              data-testid="demo-lead-phone-input"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="h-11"
              placeholder="+91 98765 43210"
            />
            {errors.phone && <p className="text-xs text-red-600">{errors.phone}</p>}
          </div>

          <Button
            type="submit"
            disabled={busy || !isSandbox}
            data-testid="demo-lead-submit-btn"
            className="w-full h-12 text-white font-semibold mt-2"
            style={{ background: accentColor }}
          >
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Saving…
              </span>
            ) : (
              'Unlock & Continue'
            )}
          </Button>

          <p className="text-[11px] text-slate-500 text-center leading-relaxed">
            We'll keep your details safe and never spam you.
          </p>
        </div>
      </form>
    </div>
  );
}

export default DemoLeadCaptureModal;
